import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users, BarChart3, CreditCard, MessageSquare, Loader2, ShieldCheck, MessageCircle, Bug, Lightbulb, Heart, CheckCircle, Eye, Mail, Star, TrendingUp, UserPlus, Send, Clock, ExternalLink, Copy } from "lucide-react";
import { toast } from "sonner";

const FEEDBACK_CATEGORIES = { bug: "Fehler", feature: "Feature-Wunsch", general: "Allgemein", praise: "Lob" };
const FEEDBACK_STATUS = { new: "Neu", read: "Gelesen", resolved: "Erledigt" };
const FEEDBACK_ICONS = { bug: Bug, feature: Lightbulb, general: MessageCircle, praise: Heart };
const CONTACT_STATUS = { new: "Neu", answered: "Beantwortet" };

export default function AdminPage() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [feedbacks, setFeedbacks] = useState([]);
  const [feedbackStats, setFeedbackStats] = useState(null);
  const [contactMessages, setContactMessages] = useState([]);
  const [contactStats, setContactStats] = useState(null);
  const [sessionFeedbacks, setSessionFeedbacks] = useState([]);
  const [sessionFeedbackStats, setSessionFeedbackStats] = useState(null);
  const [npsData, setNpsData] = useState(null);
  const [invites, setInvites] = useState([]);
  const [inviteName, setInviteName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteDays, setInviteDays] = useState(30);
  const [inviteSending, setInviteSending] = useState(false);

  const loadInvites = async () => {
    try {
      const res = await api.get("/admin/berater-invites");
      setInvites(res.data || []);
    } catch { toast.error("Einladungen konnten nicht geladen werden"); }
  };

  const sendInvite = async () => {
    if (!inviteName.trim() || !inviteEmail.trim()) { toast.error("Name und E-Mail erforderlich"); return; }
    setInviteSending(true);
    try {
      const res = await api.post("/admin/invite-berater", { name: inviteName.trim(), email: inviteEmail.trim(), duration_days: inviteDays });
      toast.success(res.data.message);
      setInviteName(""); setInviteEmail(""); setInviteDays(30);
      loadInvites();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Einladung fehlgeschlagen");
    }
    setInviteSending(false);
  };

  const resendInvite = async (inviteId) => {
    try {
      const res = await api.post(`/admin/berater-invites/${inviteId}/resend`);
      toast.success(res.data.message);
      loadInvites();
    } catch (err) { toast.error(err.response?.data?.detail || "Erneut senden fehlgeschlagen"); }
  };

  const loadFeedback = async () => {
    try {
      const res = await api.get("/admin/beta-feedback");
      setFeedbacks(res.data.feedbacks || []);
      setFeedbackStats(res.data.stats || null);
    } catch { toast.error("Beta-Feedback konnte nicht geladen werden"); }
    try {
      const sfRes = await api.get("/admin/session-feedback");
      setSessionFeedbacks(sfRes.data.feedbacks || []);
      setSessionFeedbackStats(sfRes.data.stats || null);
    } catch { toast.error("Session-Feedback konnte nicht geladen werden"); }
    try {
      const npsRes = await api.get("/admin/nps");
      setNpsData(npsRes.data);
    } catch { toast.error("NPS-Daten konnten nicht geladen werden"); }
  };

  const loadMessages = async () => {
    try {
      const res = await api.get("/admin/contact-messages");
      setContactMessages(res.data.messages || []);
      setContactStats(res.data.stats || null);
    } catch { toast.error("Nachrichten konnten nicht geladen werden"); }
  };

  useEffect(() => {
    const fetch = async () => {
      try {
        const [statsRes, usersRes] = await Promise.all([api.get("/admin/stats"), api.get("/admin/users")]);
        setStats(statsRes.data);
        setUsers(usersRes.data);
      } catch (e) {
        toast.error(e.response?.status === 403 ? "Kein Admin-Zugang" : "Fehler beim Laden");
      }
      setLoading(false);
    };
    fetch();
  }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (!stats) return <div className="text-center py-20 font-body text-muted-foreground" data-testid="admin-no-access">Kein Zugang zum Admin-Bereich</div>;

  return (
    <div className="page-transition space-y-6" data-testid="admin-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Admin-Dashboard</h1>
        <p className="font-body text-sm text-muted-foreground mt-1">Übersicht und Verwaltung</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Nutzer gesamt", value: stats.total_users, icon: Users, color: "text-primary" },
          { label: "Sessions gesamt", value: stats.total_sessions, icon: MessageSquare, color: "text-info" },
          { label: "Abgeschlossen", value: stats.completed_sessions, icon: BarChart3, color: "text-success" },
          { label: "Umsatz (€)", value: `€${(stats.total_revenue || 0).toFixed(2)}`, icon: CreditCard, color: "text-warning" },
        ].map((s, i) => (
          <Card key={i} className="border-border/50" data-testid={`admin-stat-${i}`}>
            <CardContent className="pt-6 pb-4">
              <s.icon className={`w-5 h-5 ${s.color} mb-2`} />
              <p className="font-heading text-2xl font-bold">{s.value}</p>
              <p className="font-body text-xs text-muted-foreground">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border pb-2 flex-wrap">
        {["overview", "users", "invites", "feedback", "messages"].map(t => (
          <Button key={t} variant={tab === t ? "secondary" : "ghost"} size="sm" className="font-body" onClick={() => { setTab(t); if (t === "feedback") loadFeedback(); if (t === "messages") loadMessages(); if (t === "invites") loadInvites(); }} data-testid={`admin-tab-${t}`}>
            {t === "overview" ? "Uebersicht" : t === "users" ? "Nutzer" : t === "invites" ? "Einladungen" : t === "feedback" ? "Feedback" : "Nachrichten"}
            {t === "feedback" && feedbackStats?.new > 0 && (
              <Badge variant="destructive" className="ml-1.5 text-[10px] px-1.5 py-0">{feedbackStats.new}</Badge>
            )}
            {t === "messages" && contactStats?.new > 0 && (
              <Badge variant="destructive" className="ml-1.5 text-[10px] px-1.5 py-0">{contactStats.new}</Badge>
            )}
          </Button>
        ))}
      </div>

      {tab === "overview" && (
        <Card className="border-border/50" data-testid="admin-recent-users">
          <CardHeader><CardTitle className="font-heading text-lg">Neueste Nutzer</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-body">Name</TableHead>
                  <TableHead className="font-body">E-Mail</TableHead>
                  <TableHead className="font-body">Anmeldetyp</TableHead>
                  <TableHead className="font-body">Registriert</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(stats.recent_users || []).slice(0, 10).map((u, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-body text-sm">{u.name}</TableCell>
                    <TableCell className="font-body text-sm text-muted-foreground">{u.email}</TableCell>
                    <TableCell><Badge variant="secondary" className="font-body text-xs">{u.auth_type || "email"}</Badge></TableCell>
                    <TableCell className="font-body text-xs text-muted-foreground">{u.created_at ? new Date(u.created_at).toLocaleDateString("de-DE") : "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {tab === "users" && (
        <Card className="border-border/50" data-testid="admin-users-list">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Alle Nutzer ({users.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-body">Name</TableHead>
                  <TableHead className="font-body">E-Mail</TableHead>
                  <TableHead className="font-body">Plan</TableHead>
                  <TableHead className="font-body">Sessions</TableHead>
                  <TableHead className="font-body">Admin</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-body text-sm">{u.name}</TableCell>
                    <TableCell className="font-body text-sm text-muted-foreground">{u.email}</TableCell>
                    <TableCell><Badge variant={u.subscription === "premium" ? "default" : "secondary"} className="font-body text-xs">{u.subscription}</Badge></TableCell>
                    <TableCell className="font-body text-sm">{u.session_count}</TableCell>
                    <TableCell>
                      {u.is_admin ? <ShieldCheck className="w-4 h-4 text-primary" /> : (
                        <Button variant="ghost" size="sm" className="font-body text-xs" onClick={async () => {
                          try { await api.post(`/admin/make-admin/${u.user_id}`); toast.success("Admin-Rechte vergeben"); } catch { toast.error("Admin-Rechte konnten nicht vergeben werden"); }
                        }} data-testid={`make-admin-${i}`}>Admin machen</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* ── Berater-Einladungen Tab ── */}
      {tab === "invites" && (
        <div className="space-y-4" data-testid="admin-invites-section">
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-primary" /> Berater einladen
              </CardTitle>
              <CardDescription className="font-body text-sm">Erstellen Sie einen kostenlosen Testzugang fuer einen MPU-Berater.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:grid-cols-3">
                <div>
                  <Label className="font-body text-xs">Name</Label>
                  <Input value={inviteName} onChange={e => setInviteName(e.target.value)} placeholder="Dr. Max Mustermann" className="mt-1 font-body text-sm" data-testid="invite-name-input" />
                </div>
                <div>
                  <Label className="font-body text-xs">E-Mail</Label>
                  <Input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="berater@example.de" className="mt-1 font-body text-sm" data-testid="invite-email-input" />
                </div>
                <div>
                  <Label className="font-body text-xs">Dauer (Tage)</Label>
                  <Input type="number" value={inviteDays} onChange={e => setInviteDays(parseInt(e.target.value) || 30)} min={7} max={365} className="mt-1 font-body text-sm" data-testid="invite-days-input" />
                </div>
              </div>
              <Button className="mt-3 font-body gap-2 bg-primary text-primary-foreground" onClick={sendInvite} disabled={inviteSending} data-testid="send-invite-btn">
                {inviteSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Einladung senden
              </Button>
            </CardContent>
          </Card>

          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Alle Einladungen ({invites.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {invites.length === 0 ? (
                <p className="font-body text-sm text-muted-foreground text-center py-8">Noch keine Einladungen erstellt.</p>
              ) : (
                <div className="space-y-3">
                  {invites.map((inv, i) => (
                    <div key={i} className="border border-border/50 rounded-lg p-4" data-testid={`invite-item-${i}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-body text-sm font-medium">{inv.name}</p>
                            {inv.used ? (
                              <Badge variant="default" className="font-body text-xs">Registriert</Badge>
                            ) : inv.is_expired ? (
                              <Badge variant="destructive" className="font-body text-xs">Abgelaufen</Badge>
                            ) : (
                              <Badge variant="secondary" className="font-body text-xs">Ausstehend</Badge>
                            )}
                            <Badge variant="outline" className="font-body text-[10px]">{inv.duration_days} Tage</Badge>
                          </div>
                          <p className="font-body text-xs text-muted-foreground mt-0.5">{inv.email}</p>
                          <div className="flex items-center gap-3 mt-1.5 text-[11px] text-muted-foreground font-body flex-wrap">
                            <span>Erstellt: {new Date(inv.created_at).toLocaleDateString("de-DE")}</span>
                            <span>Ablauf: {new Date(inv.expires_at).toLocaleDateString("de-DE")}</span>
                            {inv.email_sent ? <span className="text-emerald-600">E-Mail gesendet</span> : <span className="text-amber-600">E-Mail nicht gesendet</span>}
                            {inv.reminder_7d_sent_at && <span className="text-blue-600">7-Tage-Erinnerung: {new Date(inv.reminder_7d_sent_at).toLocaleDateString("de-DE")}</span>}
                            {inv.expired_email_sent_at && <span className="text-red-600">Ablauf-Mail: {new Date(inv.expired_email_sent_at).toLocaleDateString("de-DE")}</span>}
                          </div>
                          {inv.user_info && (
                            <div className="mt-2 bg-muted/30 rounded-md p-2 text-[11px] font-body">
                              <div className="flex items-center gap-3 flex-wrap">
                                <span>Reg.: {inv.user_info.registered_at ? new Date(inv.user_info.registered_at).toLocaleDateString("de-DE") : "-"}</span>
                                <span>Sessions: <strong>{inv.user_info.sessions}</strong></span>
                                {inv.user_info.last_activity && <span>Letzte Aktivitaet: {new Date(inv.user_info.last_activity).toLocaleDateString("de-DE")}</span>}
                                {inv.user_info.feedback_given && <Badge variant="default" className="text-[10px] py-0">Feedback gegeben</Badge>}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          {!inv.used && (
                            <Button variant="ghost" size="sm" className="font-body text-xs h-7 px-2" onClick={() => resendInvite(inv.invite_id)} data-testid={`resend-invite-${i}`}>
                              <Send className="w-3 h-3 mr-1" /> Erneut
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {tab === "feedback" && (
        <div className="space-y-4" data-testid="admin-feedback-section">
          {feedbackStats && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { label: "Gesamt", value: feedbackStats.total, icon: MessageCircle },
                { label: "Neu", value: feedbackStats.new, icon: Eye },
                { label: "Fehler", value: feedbackStats.bugs, icon: Bug },
                { label: "Feature-Wünsche", value: feedbackStats.features, icon: Lightbulb },
              ].map((s, i) => (
                <Card key={i} className="border-border/50">
                  <CardContent className="pt-4 pb-3 flex items-center gap-3">
                    <s.icon className="w-4 h-4 text-primary" />
                    <div>
                      <p className="font-heading text-xl font-bold">{s.value}</p>
                      <p className="font-body text-xs text-muted-foreground">{s.label}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          <Card className="border-border/50">
            <CardHeader><CardTitle className="font-heading text-lg">Beta-Feedback ({feedbacks.length})</CardTitle></CardHeader>
            <CardContent>
              {feedbacks.length === 0 ? (
                <p className="font-body text-sm text-muted-foreground text-center py-8">Noch kein Feedback eingegangen.</p>
              ) : (
                <div className="space-y-3">
                  {feedbacks.map((fb, i) => {
                    const Icon = FEEDBACK_ICONS[fb.category] || MessageCircle;
                    return (
                      <div key={i} className="border border-border/50 rounded-lg p-4" data-testid={`feedback-item-${i}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 flex-1 min-w-0">
                            <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${fb.category === "bug" ? "text-error" : fb.category === "praise" ? "text-success" : "text-primary"}`} />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge variant="secondary" className="font-body text-xs">{FEEDBACK_CATEGORIES[fb.category]}</Badge>
                                <Badge variant={fb.status === "new" ? "destructive" : fb.status === "resolved" ? "default" : "secondary"} className="font-body text-xs">{FEEDBACK_STATUS[fb.status]}</Badge>
                                {fb.rating > 0 && (
                                  <span className="font-body text-xs text-warning">{"★".repeat(fb.rating)}{"☆".repeat(5 - fb.rating)}</span>
                                )}
                              </div>
                              {fb.message && <p className="font-body text-sm mt-1.5 leading-relaxed">{fb.message}</p>}
                              <p className="font-body text-[11px] text-muted-foreground mt-1.5">
                                {fb.user_name} ({fb.user_email}) — {fb.page || "/"} — {new Date(fb.created_at).toLocaleString("de-DE")}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            {fb.status === "new" && (
                              <Button variant="ghost" size="sm" className="font-body text-xs h-7 px-2" onClick={async () => {
                                try { await api.put(`/admin/beta-feedback/${fb.feedback_id}/status?status=read`); loadFeedback(); } catch { toast.error("Status konnte nicht aktualisiert werden"); }
                              }} data-testid={`feedback-mark-read-${i}`}>
                                <Eye className="w-3 h-3 mr-1" /> Gelesen
                              </Button>
                            )}
                            {fb.status !== "resolved" && (
                              <Button variant="ghost" size="sm" className="font-body text-xs h-7 px-2 text-success" onClick={async () => {
                                try { await api.put(`/admin/beta-feedback/${fb.feedback_id}/status?status=resolved`); loadFeedback(); } catch { toast.error("Status konnte nicht aktualisiert werden"); }
                              }} data-testid={`feedback-resolve-${i}`}>
                                <CheckCircle className="w-3 h-3 mr-1" /> Erledigt
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Session Feedback Section */}
          <Card className="border-border/50">
            <CardHeader><CardTitle className="font-heading text-lg">Session-Feedback</CardTitle></CardHeader>
            <CardContent>
              {sessionFeedbackStats && (
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="text-center p-3 bg-muted/30 rounded-lg">
                    <p className="font-heading text-xl font-bold">{sessionFeedbackStats.total}</p>
                    <p className="font-body text-xs text-muted-foreground">Gesamt</p>
                  </div>
                  <div className="text-center p-3 bg-muted/30 rounded-lg">
                    <p className="font-heading text-xl font-bold text-warning">{sessionFeedbackStats.avg_stars}<Star className="w-3.5 h-3.5 inline ml-0.5 text-warning fill-warning" /></p>
                    <p className="font-body text-xs text-muted-foreground">Sterne</p>
                  </div>
                  <div className="text-center p-3 bg-muted/30 rounded-lg">
                    <p className="font-heading text-xl font-bold text-success">{sessionFeedbackStats.ki_helpful_pct}%</p>
                    <p className="font-body text-xs text-muted-foreground">KI hilfreich</p>
                  </div>
                </div>
              )}
              {sessionFeedbacks.length === 0 ? (
                <p className="font-body text-sm text-muted-foreground text-center py-4">Noch kein Session-Feedback.</p>
              ) : (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {sessionFeedbacks.slice(0, 20).map((sf, i) => (
                    <div key={i} className="border border-border/50 rounded-lg p-3 text-sm" data-testid={`session-fb-${i}`}>
                      <div className="flex items-center gap-2">
                        <span className="font-body text-xs text-warning">{"★".repeat(sf.stars)}{"☆".repeat(5 - sf.stars)}</span>
                        <Badge variant={sf.ki_helpful ? "default" : "secondary"} className="font-body text-[10px]">
                          KI {sf.ki_helpful ? "hilfreich" : "nicht hilfreich"}
                        </Badge>
                        <span className="font-body text-[10px] text-muted-foreground ml-auto">{new Date(sf.created_at).toLocaleString("de-DE")}</span>
                      </div>
                      {sf.comment && <p className="font-body text-xs mt-1 text-muted-foreground">{sf.comment}</p>}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* NPS Section */}
          <Card className="border-border/50">
            <CardHeader><CardTitle className="font-heading text-lg">NPS-Score</CardTitle></CardHeader>
            <CardContent>
              {npsData?.stats && npsData.stats.total > 0 ? (
                <>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                    <div className="text-center p-3 bg-muted/30 rounded-lg">
                      <p className={`font-heading text-2xl font-bold ${npsData.stats.nps_score >= 50 ? "text-success" : npsData.stats.nps_score >= 0 ? "text-warning" : "text-error"}`}>{npsData.stats.nps_score}</p>
                      <p className="font-body text-xs text-muted-foreground">NPS Score</p>
                    </div>
                    <div className="text-center p-3 bg-success/5 rounded-lg">
                      <p className="font-heading text-xl font-bold text-success">{npsData.stats.promoters}</p>
                      <p className="font-body text-xs text-muted-foreground">Promoter (9-10)</p>
                    </div>
                    <div className="text-center p-3 bg-warning/5 rounded-lg">
                      <p className="font-heading text-xl font-bold text-warning">{npsData.stats.passives}</p>
                      <p className="font-body text-xs text-muted-foreground">Passiv (7-8)</p>
                    </div>
                    <div className="text-center p-3 bg-error/5 rounded-lg">
                      <p className="font-heading text-xl font-bold text-error">{npsData.stats.detractors}</p>
                      <p className="font-body text-xs text-muted-foreground">Detractor (0-6)</p>
                    </div>
                  </div>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {npsData.responses.slice(0, 15).map((r, i) => (
                      <div key={i} className="border border-border/50 rounded-lg p-3 flex items-start gap-3" data-testid={`nps-entry-${i}`}>
                        <Badge variant={r.score >= 9 ? "default" : r.score >= 7 ? "secondary" : "destructive"} className="font-body text-xs">{r.score}/10</Badge>
                        <div className="flex-1 min-w-0">
                          {r.comment && <p className="font-body text-xs">{r.comment}</p>}
                          <p className="font-body text-[10px] text-muted-foreground mt-0.5">{new Date(r.created_at).toLocaleString("de-DE")} — {r.sessions_at_time} Sessions</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="font-body text-sm text-muted-foreground text-center py-4">Noch keine NPS-Antworten.</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Nachrichten Tab */}
      {tab === "messages" && (
        <div className="space-y-4" data-testid="admin-messages-section">
          {contactStats && (
            <div className="grid grid-cols-2 gap-3">
              <Card className="border-border/50">
                <CardContent className="pt-4 pb-3 flex items-center gap-3">
                  <Mail className="w-4 h-4 text-primary" />
                  <div>
                    <p className="font-heading text-xl font-bold">{contactStats.total}</p>
                    <p className="font-body text-xs text-muted-foreground">Gesamt</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-border/50">
                <CardContent className="pt-4 pb-3 flex items-center gap-3">
                  <Eye className="w-4 h-4 text-warning" />
                  <div>
                    <p className="font-heading text-xl font-bold">{contactStats.new}</p>
                    <p className="font-body text-xs text-muted-foreground">Unbeantwortet</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
          <Card className="border-border/50">
            <CardHeader><CardTitle className="font-heading text-lg">Kontaktnachrichten</CardTitle></CardHeader>
            <CardContent>
              {contactMessages.length === 0 ? (
                <p className="font-body text-sm text-muted-foreground text-center py-8">Noch keine Nachrichten eingegangen.</p>
              ) : (
                <div className="space-y-3">
                  {contactMessages.map((msg, i) => (
                    <div key={i} className="border border-border/50 rounded-lg p-4" data-testid={`contact-msg-${i}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-body text-sm font-medium">{msg.name}</p>
                            <Badge variant={msg.status === "new" ? "destructive" : "default"} className="font-body text-xs">{CONTACT_STATUS[msg.status]}</Badge>
                          </div>
                          <p className="font-body text-xs text-muted-foreground mt-0.5">{msg.email} — {msg.subject}</p>
                          <p className="font-body text-sm mt-2 leading-relaxed">{msg.message}</p>
                          <p className="font-body text-[10px] text-muted-foreground mt-1.5">{new Date(msg.created_at).toLocaleString("de-DE")}</p>
                        </div>
                        <div className="flex-shrink-0">
                          {msg.status === "new" && (
                            <Button variant="ghost" size="sm" className="font-body text-xs h-7 px-2 text-success" onClick={async () => {
                              try { await api.put(`/admin/contact-messages/${msg.contact_id}/status?status=answered`); loadMessages(); } catch { toast.error("Status konnte nicht aktualisiert werden"); }
                            }} data-testid={`msg-mark-answered-${i}`}>
                              <CheckCircle className="w-3 h-3 mr-1" /> Beantwortet
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
